import { getCountryName } from './countries';

// Shared display formatters. Used by every list page (customers, quotes,
// jobs, ...) so date / currency rendering stays consistent.

// For SERVER TIMESTAMPS (CreatedAt, UpdatedAt, etc.) — ISO-8601 strings
// with a Z suffix. Converts to the browser's local calendar date.
export const formatInstant = (value) =>
    value ? new Date(value).toLocaleDateString() : '';

// For CALENDAR DATES (QuotedAt, StartDate, etc.) — "yyyy-MM-dd" strings
// with no timezone. Renders locale-formatted WITHOUT applying any TZ
// shift, so a quote dated 13-Apr always displays as 13-Apr regardless
// of where the user is.
export const formatDate = (value) => {
    if (!value) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return '';
    const [, y, m, d] = match;
    // Local-construct so toLocaleDateString stays locale-respectful
    // (en-GB → dd/MM/yyyy) without doing any UTC conversion.
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString();
};

// Per-currency Intl.NumberFormat cache. Intl.NumberFormat construction is
// expensive and we render hundreds of cells per list page, so we memoise
// one formatter per currency code. Locale stays en-GB so number grouping
// and spacing match the rest of the UI; only the currency symbol changes.
// Returns '' for null/undefined so empty cells stay empty rather than
// rendering "£NaN" / "€NaN" / …
const currencyFormatters = new Map();
const getCurrencyFormatter = (code) => {
    // Guard against non-string codes: DataTable renderers receive
    // (value, row), so a bare `render: formatCurrency` call-site would
    // pass the row object as `code` and Intl.NumberFormat would throw.
    // Fall back to GBP instead of crashing the whole table.
    const key = typeof code === 'string' && code ? code : 'GBP';
    let fmt = currencyFormatters.get(key);
    if (!fmt) {
        fmt = new Intl.NumberFormat('en-GB', { style: 'currency', currency: key });
        currencyFormatters.set(key, fmt);
    }
    return fmt;
};

export const formatCurrency = (value, code = 'GBP') =>
    value == null ? '' : getCurrencyFormatter(code).format(value);

// ISO 3166-1 alpha-2 → display name (e.g. "GB" → "United Kingdom"). Falls
// back to the raw code if unknown so no cell ever renders as blank for a
// value that's actually present in the row.
export const formatCountry = (value) => getCountryName(value);

// File size in bytes → human-readable ("1.2 MB", "345 KB", "42 B"). Used for
// receipt rows. Picks the largest unit that keeps the integer part single-
// digit-ish; anything over a GB is probably wrong but we handle it anyway.
export const formatFileSize = (bytes) => {
    if (bytes == null || bytes < 0) return '';
    if (bytes < 1024)               return `${bytes} B`;
    if (bytes < 1024 * 1024)        return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};
